import * as React from 'react';
import Button from '@navi/web-ui/lib/primitives/Button/Button';
import AddIcon from '@icons/AddIcon';
import styles from './index.module.scss';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import Typography from '@navi/web-ui/lib/primitives/Typography/Typography';
import { getValidateNumber, postAlternateNumber } from '../../actions/moreDetailsActions';
import { createKey } from 'src/utils/CaseDetail.utils';
import { addClickstreamEvent } from '../../../../service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '../../../../service/clickStream.constant';
import Modal from '@cp/src/components/Modal/Modal';
import { isValidPhoneNumber } from '@cp/src/utils/commonUtils';
import { RootState } from '@cp/src/store';
import { Roles } from '@cp/pages/auth/constants/AuthConstants';
import { useForm } from 'react-hook-form';
import cx from 'classnames';
import BorderedInputTextBox from './BorderedInputTextBox';
import { getCustomerConfidence, getTelephonesV2 } from '../../actions/casesDetailsActions';
import SingleAutoDropdown from './SingleAutoDropdown';
import {
  CUSTOMER_CONFIDENCE,
  OTHER_SOURCE,
  OtherSourceValidations,
  PHONE_NUMBER,
  PhoneNumberValidations,
  SOURCE
} from '../../constants/MoreDetails.constant';
import { ICustomerConfidenceLevel } from '../../interfaces/CaseDetail.type';
import { Chip } from '@navi/web-ui/lib/primitives';
import { convertToRadioOptions } from './utils/utils';
import ProofOfNumberSourceContainer from './ProofOfNumberSourceContainer';

export interface AddNumberProps {
  phoneNumber: string;
  source: string;
  otherSource: string;
  customerConfidence: string;
}

const AddNewNumberOverlay: React.FC<React.PropsWithChildren> = () => {
  const {
    loanId = '',
    customerId = '',
    caseBusinessVertical = '',
    caseReferenceId = ''
  } = useParams();
  const editAccessFlag = useSelector(
    (state: RootState) =>
      state?.caseDetail?.pageData?.[createKey(loanId, customerId)]?.details?.data?.editAccessFlag
  );
  const user = useSelector((state: RootState) => state.common.userData);
  const isGlobalAccessRoleGiven = user?.roles?.includes(Roles.ROLE_GLOBAL_ACCESS);
  const isCiuAgent = useSelector((state: RootState) => state.common.isCiuAgent);
  const disableCTAs = isGlobalAccessRoleGiven ? !editAccessFlag : false;
  const [isDisabledSubmitButton, setIsDisabledSubmitButton] = useState(false);

  const customerConfidence = useSelector((state: RootState) => state.caseDetail.customerConfidence);
  const telephoneCustomerConfidence = customerConfidence?.telephoneCustomerConfidence;
  const secondarySources = customerConfidence?.longhornSecondarySources;

  const radioOptionsList = convertToRadioOptions(telephoneCustomerConfidence);

  const optionsList =
    secondarySources?.map(source => ({
      label: source.label,
      value: source.value
    })) || [];

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [proofOfSourceFile, setProofOfSourceFile] = useState<File | null>(null);
  const [proofOfSourceError, setProofOfSourceError] = useState<string>('');

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid, isDirty },
    register,
    control,
    getValues,
    reset
  } = useForm<AddNumberProps>({ mode: 'onChange' });
  const dispatch = useDispatch();

  const openModal = () => {
    setIsModalOpen(true);
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Phone_AddNewNumberCTA);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleFormSubmit = data => {};
  const handleAddNumber = async () => {
    if (!(!isDirty || !isValid)) {
      setIsDisabledSubmitButton(true);
      dispatch(
        postAlternateNumber({
          loanaccountnumber: loanId,
          customerreferenceid: customerId,
          caseReferenceId,
          caseBusinessVertical,
          alternateNumber: getValues(PHONE_NUMBER),
          source: getValues(SOURCE).value,
          successCallback: successCallback,
          cb: setIsModalOpen,
          otherSource: getValues(OTHER_SOURCE),
          customerConfidence: watch('customerConfidence'),
          proofOfSource: proofOfSourceFile
        })
      );
    }
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Phone_AddNewNumberSubmit, {
      newPhoneNumber: getValues(PHONE_NUMBER)
    });
  };

  const successCallback = useCallback(() => {
    dispatch(getTelephonesV2(loanId, customerId, caseReferenceId, caseBusinessVertical));
    setIsDisabledSubmitButton(false);
    reset();
  }, []);

  const handleNumberChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
  }, []);

  const selectedSource = watch(SOURCE, '');
  const enteredOtherSource = watch(OTHER_SOURCE, '');
  const selectedValue = watch(CUSTOMER_CONFIDENCE, '');

  const validateNumber = async (value: string) => {
    if (value.length == 10) {
      if (!isValidPhoneNumber(value)) return PhoneNumberValidations.VALID_PHONE_NUMBER;
      const errorMessage = await getValidateNumber(value, customerId);
      if (errorMessage === 'Error') {
        return PhoneNumberValidations.ERROR;
      }
      if (errorMessage?.isPresent) {
        if (errorMessage?.isValid) {
          return PhoneNumberValidations.PHONE_NUMBER_EXISTS;
        } else return PhoneNumberValidations.NUMBER_MARKED_INVALID;
      }
      return true;
    }
    return PhoneNumberValidations.VALID_PHONE_NUMBER;
  };
  const validateOtherSource = (value: string) => {
    if (value?.length < 3) return OtherSourceValidations.MIN_SOURCE_LENGTH;
    const areAllCharactersSame = value.split('').every(char => char === value[0]);
    if (areAllCharactersSame) return OtherSourceValidations.SAME_CHARACTERS;
    return /^(?=.*[a-zA-Z0-9]).+$/i.test(value) || OtherSourceValidations.ONLY_SPECIAL_CHARACTERS;
  };

  const handleSelectionChange = selectedOption => {
    setValue(SOURCE, selectedOption.value);
  };

  const shouldDisableForProof = isCiuAgent && (!proofOfSourceFile || proofOfSourceError);

  const isButtonDisabled =
    !isDirty ||
    !isValid ||
    !isValidPhoneNumber(getValues(PHONE_NUMBER)) ||
    (isCiuAgent && !selectedValue) ||
    shouldDisableForProof ||
    isDisabledSubmitButton;

  return (
    <div className={styles.addNewNumberOverlay}>
      <Button
        variant={'text'}
        onClick={() => {
          if (disableCTAs) return;
          dispatch(getCustomerConfidence());
          openModal();
        }}
        disabled={disableCTAs}
        startAdornment={<AddIcon color={'#0276FE'} />}
      >
        Add new number
      </Button>
      <Modal
        visible={isModalOpen}
        contentWrapperClass="w-[490px]"
        onClose={closeModal}
        header="Add new number"
      >
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <div className={styles.addNewNumberCard}>
            <Typography className={styles.enterPhoneNumber} variant={'p4'}>
              Mobile number
            </Typography>

            <BorderedInputTextBox
              control={control}
              maxLength={10}
              name="phoneNumber"
              rules={{
                validate: (value: string) => {
                  if (watch(PHONE_NUMBER)) {
                    return validateNumber(value);
                  }
                }
              }}
              onChange={handleNumberChange}
              placeholder="Enter phone number"
              type="string"
              errors={errors}
              containerClassName="w-[100%]"
              isRequired={true}
            />
            <Typography className={cx(styles.enterPhoneNumber, 'my-[8px]')} variant={'p4'}>
              Source of number
            </Typography>
            <SingleAutoDropdown
              optionsList={optionsList}
              selectedSource={selectedSource}
              handleSelectionChange={handleSelectionChange}
              placeholder="Enter or select source"
              errors={errors}
              control={control}
              name="source"
              rules={{
                required: true
              }}
            />
            {selectedSource?.value === 'OTHER_SOURCES' && (
              <>
                <Typography className={cx(styles.enterPhoneNumber, 'mt-[24px]')} variant={'p4'}>
                  Enter other source
                </Typography>

                <BorderedInputTextBox
                  placeholder="Enter a valid source"
                  type="string"
                  errors={errors}
                  maxLength={30}
                  containerClassName="w-[100%]"
                  RightInputAdornment={
                    <div className="mt-[25px] flex row mb-[6px]">
                      <Typography variant={'p4'} className="text-grayscale-3 text-sm">
                        {enteredOtherSource?.length}/30
                      </Typography>
                    </div>
                  }
                  control={control}
                  name="otherSource"
                  rules={{
                    required: true,
                    validate: (value: string) => {
                      return validateOtherSource(value);
                    }
                  }}
                />
              </>
            )}

            {isCiuAgent ? (
              <>
                <div>
                  <Typography className={cx(styles.enterPhoneNumber, 'mt-4 mb-2')} variant={'p4'}>
                    Confidence on Number
                  </Typography>

                  <div className="flex flex-wrap gap-2">
                    {radioOptionsList.map(optionType => (
                      <Chip
                        key={optionType.value}
                        label={optionType.label}
                        wrapperClasses={cx([
                          styles.chip,
                          { [styles.chipActiveColor]: optionType.value === selectedValue }
                        ])}
                        labelClasses={styles.text}
                        onChipClick={() => {
                          setValue('customerConfidence', optionType.value);
                        }}
                      />
                    ))}
                  </div>
                </div>
                <ProofOfNumberSourceContainer
                  proofOfSourceFile={proofOfSourceFile}
                  setProofOfSourceFile={setProofOfSourceFile}
                  setProofOfSourceError={setProofOfSourceError}
                  proofOfSourceError={proofOfSourceError}
                />
                {proofOfSourceError && (
                  <Typography variant="p5" className="text-red-500 mt-2">
                    {proofOfSourceError}
                  </Typography>
                )}
              </>
            ) : null}

            <div className="flex justify-end mt-[30px]">
              <Button className="!w-[173px]" disabled={isButtonDisabled} onClick={handleAddNumber}>
                Add new number
              </Button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};
export default AddNewNumberOverlay;
